import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Sparkles, AlertTriangle } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { sendChatMessage } from "@bcc/shared-api";
import type {
  ChatMessage,
  ConversationMessage,
  RateLimitError,
} from "@bcc/shared-types";
import { Button, ChatBubble, Card } from "@bcc/shared-ui";
import { v4 as uuidv4 } from "uuid";
import { Captcha, useCaptcha } from "../components/Captcha";
import { SEO } from "../components/SEO";
import { CHAT_PAGE_FAQS } from "../seo/structured-data";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8002";

// Fallback categories shown when the trending API is unavailable
const FALLBACK_CATEGORIES = [
  {
    name: "Building Code",
    icon: "🏗️",
    questions: [
      {
        question: "What is the minimum stair width for residential buildings?",
      },
      { question: "What are the egress window requirements for bedrooms?" },
      { question: "What's the minimum ceiling height for a habitable room?" },
    ],
  },
  {
    name: "Fire & Safety",
    icon: "🔥",
    questions: [
      {
        question:
          "What fire separation is required between a garage and house?",
      },
      { question: "Where do I need smoke alarms in a house?" },
      { question: "What's the fire rating for a secondary suite?" },
    ],
  },
  {
    name: "Zoning",
    icon: "🏘️",
    questions: [
      { question: "What is the maximum building height in R-C1 zone?" },
      { question: "What are the setback requirements for R-C2?" },
      { question: "What's the maximum lot coverage allowed?" },
    ],
  },
  {
    name: "Permits",
    icon: "📋",
    questions: [
      { question: "What permits do I need for a basement development?" },
      { question: "Do I need a permit to build a deck?" },
      { question: "What's required for a secondary suite permit?" },
    ],
  },
  {
    name: "SDAB & Appeals",
    icon: "⚖️",
    questions: [
      { question: "How do I appeal a development permit decision?" },
      { question: "What are valid grounds for an SDAB appeal?" },
      { question: "Can I get a variance for my setback?" },
    ],
  },
  {
    name: "Plumbing & Mechanical",
    icon: "🔧",
    questions: [
      { question: "What's the minimum slope for a drain pipe?" },
      { question: "How many fixture units can a 3-inch drain handle?" },
      { question: "What ventilation is required for a bathroom?" },
    ],
  },
];

interface TrendingQuestion {
  id?: string;
  question: string;
  category?: string;
  ask_count?: number;
  click_count?: number;
}

interface QuestionCategory {
  name: string;
  icon?: string;
  questions: TrendingQuestion[];
}

export function ChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<string>(() => uuidv4());
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    retryAfter?: number;
    message?: string;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Trending questions state - separate from sample categories
  const [trendingQuestions, setTrendingQuestions] = useState<
    TrendingQuestion[]
  >([]);
  // Always use FALLBACK_CATEGORIES for the sample questions
  const sampleCategories = FALLBACK_CATEGORIES;

  // Captcha state for bot protection
  const {
    captchaRequired,
    captchaToken,
    handleCaptchaVerify,
    handleRateLimitError,
    resetCaptcha,
  } = useCaptcha();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch trending questions on mount (shown separately from sample categories)
  useEffect(() => {
    fetch(`${API_BASE}/api/v1/trending/questions?limit=6&days=30`)
      .then((r) => r.json())
      .then((data) => {
        if (data.categories && data.categories.length > 0) {
          // Flatten all trending questions from all categories
          const allTrending: TrendingQuestion[] = [];
          data.categories.forEach((cat: QuestionCategory) => {
            cat.questions.forEach((q) => {
              allTrending.push({
                ...q,
                category: cat.name,
              });
            });
          });
          // Take top 4 trending questions
          setTrendingQuestions(allTrending.slice(0, 4));
        }
      })
      .catch(() => {
        // Silently ignore - sample categories will still show
      });
  }, []);

  const chatMutation = useMutation({
    mutationFn: sendChatMessage,
    onSuccess: (response) => {
      const assistantMessage: ChatMessage = {
        id: uuidv4(),
        role: "assistant",
        content: response.response,
        timestamp: new Date().toISOString(),
        citations: response.citations,
      };
      setMessages((prev) => [...prev, assistantMessage]);
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }
    },
    onError: (
      error: Error & { response?: { status?: number; data?: RateLimitError } },
    ) => {
      // Check for rate limit errors (429 status)
      if (
        error.response?.status === 429 ||
        error.message.includes("rate limit") ||
        error.message.includes("Rate limit")
      ) {
        const rateLimitData = error.response?.data;

        // Handle captcha requirement
        if (rateLimitData?.captcha_required) {
          handleRateLimitError({ captcha_required: true });
          setRateLimitInfo({
            message: "Please verify you're human to continue.",
          });
          return;
        }

        // Handle regular rate limit
        setRateLimitInfo({
          retryAfter: rateLimitData?.retry_after_seconds,
          message:
            rateLimitData?.error ||
            "Too many requests. Please wait before trying again.",
        });
        return;
      }

      // Regular error handling
      const errorMessage: ChatMessage = {
        id: uuidv4(),
        role: "assistant",
        content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    },
  });

  // Build conversation history from messages for context
  // Keeps full history for <=3 exchanges, backend will compress older ones
  const buildHistory = useCallback(
    (currentMessages: ChatMessage[]): ConversationMessage[] => {
      return currentMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));
    },
    [],
  );

  const handleSend = () => {
    if (!input.trim() || chatMutation.isPending) return;

    // Clear any previous rate limit info
    setRateLimitInfo(null);

    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: "user",
      content: input.trim(),
      timestamp: new Date().toISOString(),
    };

    // Build history from existing messages before adding the new user message
    const history = buildHistory(messages);

    setMessages((prev) => [...prev, userMessage]);
    chatMutation.mutate({
      message: input.trim(),
      conversation_id: conversationId,
      history: history,
      captcha_token: captchaToken || undefined,
    });
    setInput("");

    // Reset captcha after successful use
    if (captchaToken) {
      resetCaptcha();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (q: TrendingQuestion) => {
    // Record click for trending questions that have an id
    if (q.id) {
      fetch(`${API_BASE}/api/v1/trending/questions/${q.id}/click`, {
        method: "POST",
      }).catch(() => {});
    }
    setInput(q.question);
    inputRef.current?.focus();
  };

  return (
    <>
      <SEO faqs={CHAT_PAGE_FAQS} />
      <div className="h-[calc(100vh-12rem)] flex flex-col">
        {/* Header - only show when there are messages */}
        {messages.length > 0 && (
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-teal-500" />
              Building Code Expert
            </h1>
          </div>
        )}

        {/* Messages area */}
        <Card
          variant="outlined"
          padding="none"
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div
            className={`flex-1 p-4 space-y-4 ${messages.length > 0 ? "overflow-y-auto" : "overflow-hidden flex flex-col"}`}
          >
            {messages.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="h-7 w-7 text-teal-500" />
                  <h1 className="text-2xl font-bold text-gray-900">
                    How can I help you today?
                  </h1>
                </div>
                <p className="text-gray-500 mb-8">
                  Ask about building codes, permits, zoning, or construction
                  requirements.
                </p>

                {/* Trending questions - shown only if we have some */}
                {trendingQuestions.length > 0 && (
                  <div className="w-full max-w-3xl mb-6">
                    <p className="text-xs text-teal-600 mb-2 font-medium flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
                      Popular questions from other users
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {trendingQuestions.map((q, idx) => (
                        <button
                          key={q.id || idx}
                          onClick={() => handleSuggestedQuestion(q)}
                          className="px-3 py-1.5 rounded-full bg-teal-50 border border-teal-200 hover:border-teal-400 hover:bg-teal-100 transition-colors text-xs text-teal-700"
                        >
                          {q.question}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sample questions by category */}
                <div className="w-full max-w-3xl">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sampleCategories.map((category) => (
                      <div
                        key={category.name}
                        className="bg-gray-50 rounded-xl p-4 border border-gray-100"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg">{category.icon}</span>
                          <span className="font-medium text-gray-800 text-sm">
                            {category.name}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {category.questions.slice(0, 2).map((q, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleSuggestedQuestion(q)}
                              className="w-full text-left p-2 rounded-lg bg-white border border-gray-200 hover:border-teal-300 hover:bg-teal-50 transition-colors text-xs text-gray-600 leading-relaxed"
                            >
                              {q.question}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.map((message) => (
                  <ChatBubble
                    key={message.id}
                    role={message.role}
                    content={message.content}
                    timestamp={new Date(message.timestamp).toLocaleTimeString()}
                    citations={message.citations}
                  />
                ))}
                {chatMutation.isPending && (
                  <ChatBubble role="assistant" content="" isLoading />
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Rate limit warning */}
          {rateLimitInfo && !captchaRequired && (
            <div className="border-t border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">{rateLimitInfo.message}</p>
                  {rateLimitInfo.retryAfter && (
                    <p className="text-sm mt-1">
                      Please wait {rateLimitInfo.retryAfter} seconds before
                      trying again.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Captcha verification */}
          {captchaRequired && (
            <div className="border-t border-gray-100 p-4">
              <Captcha
                onVerify={(token) => {
                  handleCaptchaVerify(token);
                  setRateLimitInfo(null);
                }}
                onError={(err) => {
                  console.error("Captcha error:", err);
                }}
              />
            </div>
          )}

          {/* Input area */}
          <div className="border-t border-gray-100 p-4 bg-gray-50">
            <div className="flex gap-3">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about building codes, permits, regulations..."
                className="flex-1 resize-none rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent min-h-[52px] max-h-32"
                rows={1}
                disabled={captchaRequired}
              />
              <Button
                onClick={handleSend}
                disabled={
                  !input.trim() || chatMutation.isPending || captchaRequired
                }
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl px-4"
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              {captchaRequired
                ? "Complete verification above to continue"
                : "Unlimited questions - no restrictions"}
            </p>
          </div>
        </Card>
      </div>
    </>
  );
}
